import { create } from 'zustand';
import { activityTypeService, ActivityType } from '@/services/activityTypeService';
import { toast } from 'sonner';

interface ActivityTypeState {
  activityTypes: ActivityType[];
  currentType: ActivityType | null;
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchActivityTypes: (activeOnly?: boolean) => Promise<void>;
  createActivityType: (data: Partial<ActivityType>) => Promise<ActivityType>;
  updateActivityType: (id: string, data: Partial<ActivityType>) => Promise<void>;
  deleteActivityType: (id: string, options?: { permanent?: boolean }) => Promise<void>;
  reorderActivityTypes: (order: { id: string; order: number }[]) => Promise<void>;
  reset: () => void;
}

const initialState = {
  activityTypes: [],
  currentType: null,
  loading: false,
  error: null,
};

export const useActivityTypeStore = create<ActivityTypeState>((set, get) => ({
  ...initialState,

  fetchActivityTypes: async (activeOnly = true) => {
    set({ loading: true, error: null });
    try {
      const response = await activityTypeService.getActivityTypes(activeOnly);
      set({
        activityTypes: response.data || [],
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nel caricamento dei tipi attività',
        loading: false,
      });
    }
  },

  createActivityType: async (data: Partial<ActivityType>) => {
    set({ loading: true, error: null });
    try {
      const response = await activityTypeService.createActivityType(data);
      const newType = response.data;
      set((state) => ({
        activityTypes: [...state.activityTypes, newType].sort((a, b) => a.order - b.order),
        loading: false,
      }));
      toast.success('Tipo attività creato con successo');
      return newType;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Errore nella creazione del tipo attività';
      set({ error: errorMsg, loading: false });
      toast.error(errorMsg);
      throw error;
    }
  },

  updateActivityType: async (id: string, data: Partial<ActivityType>) => {
    set({ loading: true, error: null });
    try {
      const response = await activityTypeService.updateActivityType(id, data);
      const updatedType = response.data;
      set((state) => ({
        activityTypes: state.activityTypes.map((t) => (t.id === id ? updatedType : t)).sort((a, b) => a.order - b.order),
        currentType: state.currentType?.id === id ? updatedType : state.currentType,
        loading: false,
      }));
      toast.success('Tipo attività aggiornato con successo');
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nell\'aggiornamento del tipo attività',
        loading: false,
      });
      toast.error(error.response?.data?.message || 'Errore nell\'aggiornamento del tipo attività');
      throw error;
    }
  },

  deleteActivityType: async (id: string, options?: { permanent?: boolean }) => {
    set({ loading: true, error: null });
    try {
      await activityTypeService.deleteActivityType(id, options);
      if (options?.permanent) {
        set((state) => ({
          activityTypes: state.activityTypes.filter((t) => t.id !== id),
          currentType: state.currentType?.id === id ? null : state.currentType,
          loading: false,
        }));
        toast.success('Tipo attività eliminato definitivamente dal database');
      } else {
        set({ loading: false });
        toast.success('Tipo attività disattivato con successo');
      }
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nell\'eliminazione del tipo attività',
        loading: false,
      });
      toast.error(error.response?.data?.message || 'Errore nell\'eliminazione del tipo attività');
      throw error;
    }
  },

  reorderActivityTypes: async (order: { id: string; order: number }[]) => {
    set({ loading: true, error: null });
    try {
      await activityTypeService.reorderActivityTypes(order);
      // Ricarica i tipi per avere l'ordine aggiornato
      await get().fetchActivityTypes(false);
      toast.success('Ordine aggiornato con successo');
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nel riordino dei tipi attività',
        loading: false,
      });
      toast.error(error.response?.data?.message || 'Errore nel riordino dei tipi attività');
      throw error;
    }
  },

  reset: () => {
    set(initialState);
  },
}));

